import { UseComboboxPropGetters } from 'downshift';
import React, {
  ChangeEventHandler,
  FocusEventHandler,
  KeyboardEventHandler,
  useCallback,
  useEffect
} from 'react';
import { SearchBarInnerWrapper } from './SearchBar.styles';
import { SearchBarEvents } from '../constants';
import { useInputContext } from '../context/InputContext';
import { Suggestion } from '../contracts';
import { SearchSuggestionWithType } from '../contracts/SearchSuggestion';
import CloseIcon from '../icons/LargeClose';
import SearchIcon from '../icons/Search';

type OwnProps = {
  focusOnInitialRender?: boolean;
  getInputProps: UseComboboxPropGetters<
    (SearchSuggestionWithType | Suggestion)[]
  >['getInputProps'];
  handleClearText: () => void;
  handleInputOnChange: ChangeEventHandler<HTMLInputElement>;
  handleInputOnFocus: FocusEventHandler<HTMLInputElement>;
  handleKeyDown: KeyboardEventHandler<HTMLInputElement>;
};

export default function SearchBarInput({
  focusOnInitialRender,
  getInputProps,
  handleClearText,
  handleInputOnChange,
  handleInputOnFocus,
  handleKeyDown
}: OwnProps) {
  const { inputRef, inputValue } = useInputContext();
  const hasInput = inputValue.length > 0;

  useEffect(() => {
    if (!inputRef.current) return;

    if (focusOnInitialRender) {
      inputRef.current.focus();
    }
  }, [focusOnInitialRender, inputRef]);

  const handleClearClick = useCallback(() => {
    inputRef.current?.focus();
    handleClearText();
  }, [handleClearText, inputRef]);

  return (
    <SearchBarInnerWrapper>
      <div className="flex pl-12 pr-8">
        <SearchIcon />
      </div>
      <input
        className="h-full w-full flex-1 border-none bg-transparent text-base outline-none"
        placeholder="Search Make, Model, or Keyword"
        type="search"
        {...getInputProps({
          ref: inputRef,
          value: inputValue,
          onFocus: handleInputOnFocus,
          onChange: handleInputOnChange,
          onKeyDown: handleKeyDown
        })}
      />

      {hasInput ? (
        <button
          aria-label="clear text"
          className="flex items-center bg-transparent p-16 text-grey-3"
          data-analytics-id={SearchBarEvents.ClearedText}
          onClick={handleClearClick}
          title="Clear Text"
          type="button"
        >
          <CloseIcon />
        </button>
      ) : null}
    </SearchBarInnerWrapper>
  );
}
